import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Eye, EyeOff, Lock } from "lucide-react";
import { analytics } from "../utils/analytics";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_51314d4e-1cb2-4f36-bbc7-4fbfbca7013e/artifacts/ln7j2e9p_Elegant%20gold%20OK%20logo%20design%20%281%29.png";

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    if (!password) {
      toast.error("Please enter your password");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/auth/login`, { email, password });
      toast.success(response.data.message);
      analytics.login('email');
      localStorage.setItem("user", JSON.stringify(response.data.user));
      // Also set vastu_user for compatibility with other components
      localStorage.setItem("vastu_user", JSON.stringify(response.data.user));
      // Dispatch event to notify all components
      window.dispatchEvent(new Event('userLogin'));
      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F9FC] to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="Occult King" className="h-16 w-auto mx-auto mb-4" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">Welcome Back</h1>
          <p className="text-gray-600 mt-1">Login to your Occult King account</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="w-16 h-16 bg-[#4AA3DF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock size={32} className="text-[#4AA3DF]" />
            </div>
            
            <p className="text-center text-gray-600 text-sm mb-4">
              Enter your email and password to sign in.
            </p>
            
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your registered email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
                data-testid="login-email"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#F37021] hover:bg-[#E06010] text-white mt-6"
              data-testid="login-submit"
            >
              {loading ? "Logging in..." : "Login"}
            </Button>
            
            <div className="text-center">
              <Link to="/forgot-password" className="text-sm text-gray-500 hover:text-[#F37021]">
                Forgot your password?
              </Link>
            </div>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            Don't have an account?{" "}
            <Link to="/signup" className="text-[#F37021] font-medium hover:underline">
              Sign Up
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
